<script setup lang="ts">
import { ref, onMounted, onUnmounted, useAttrs } from 'vue';

type Emits = {
  (e: 'truncated', didTruncate: boolean): void;
}

const props = defineProps<{ text: string }>();
const emit = defineEmits<Emits>();

const attrs = useAttrs();

const parentRef = ref<HTMLDivElement>();
const initialParentWidth = ref<number>();
const observer = ref<ResizeObserver>();

const calculateTextWidth = (input: string) => {
  const tempEl = document.createElement('span');
  tempEl.style.textWrap = 'nowrap';
  appendBoundClasses(tempEl);
  tempEl.textContent = input;
  document.body.appendChild(tempEl);
  const { width } = tempEl.getBoundingClientRect();
  document.body.removeChild(tempEl);
  return width;
};

const appendBoundClasses = (el: HTMLSpanElement) => {
  if (!attrs?.class) return;
  const classes = (attrs['class'] as string).split(' ');
  if (!Array.isArray(classes)) return;
  classes.forEach(c => el.classList.add(c));
};


const shouldTruncate = (input: string) => {
  if (!parentRef.value || !input) return false;
  const initialTextWidth = calculateTextWidth(input);

  const parentWidth = getParentWidth();

  if (initialTextWidth < parentWidth) {
    return false;
  }

  return true;
};

const getMaxDisplayableText = (input: string) => {
  let text = `${input}&hellip;`;
  let charsToRemove = 1;
  while (shouldTruncate(text)) {
    if (charsToRemove >= input.length) break;
    text = `${input.substring(0, input.length - charsToRemove)}&hellip;`;
    charsToRemove += 1;
  }

  return text;
};

const renderTextElement = (text: string) => {
  if (!parentRef.value) return;
  const textEl = document.createElement('span');
  textEl.style.textWrap = 'nowrap';
  textEl.innerHTML = text;
  appendBoundClasses(textEl);
  parentRef.value.replaceChildren(textEl);
};

const getParentWidth = () => {
  if (!parentRef.value) return 0;
  const { width } = parentRef.value.getBoundingClientRect();
  return width;
};

const setup = () => {
  const needsToTruncate = shouldTruncate(props.text);

  renderTextElement(
    needsToTruncate ? getMaxDisplayableText(props.text) : props.text,
  );

  emit('truncated', needsToTruncate);
};

onMounted(() => {
  setup();
  observer.value = new ResizeObserver(() => {
    setup();
  });

  initialParentWidth.value = getParentWidth();

  observer.value.observe(parentRef.value);
});

onUnmounted(() => observer.value.disconnect());
</script>
<template>
  <div ref="parentRef" class="w-full overflow-x-hidden" />
</template>
